import { useState, useEffect } from 'react';
import { Language, getTranslation } from '@/lib/i18n';
import { alternatePath, hasTwin, localeOf } from '@/lib/locale-routes';

export function useLanguage() {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Inside the twinned tree the URL is authoritative: `/es/about` IS the
    // Spanish page, so the button must read "EN" there even for a visitor whose
    // browser is English. Reading this from state alone is what made the toggle
    // offer "ES" to someone already reading Spanish.
    if (localeOf(window.location.pathname) === 'es') {
      setLanguage('es');
      return;
    }

    // Outside it the URL says nothing about language, so the old signals stand:
    // an explicit ?lang, else the browser preference.
    const langParam = new URLSearchParams(window.location.search).get('lang');
    if (langParam === 'es') {
      setLanguage('es');
      return;
    }
    if (navigator.language.toLowerCase().startsWith('es')) {
      setLanguage('es');
    }
  }, []);

  const toggleLanguage = () => {
    const path = window.location.pathname;

    // Switching language is a navigation, not a state flip. The Spanish copy of
    // a page is a different file at a different route; setting a flag on the
    // English page cannot render it, which is why this button did nothing
    // anywhere except the homepage.
    if (hasTwin(path)) {
      const url = new URL(window.location.href);
      url.pathname = alternatePath(path);
      // Drop the legacy ?lang: vercel.json redirects `/` + `?lang=es` to `/es/`,
      // and carrying it to a path-prefix URL re-creates the documented 4-hop
      // chain this repo already fixed once in #12.
      url.searchParams.delete('lang');
      window.location.assign(url.toString());
      return;
    }

    // No twin (legacy WordPress paths, /thank-you, /tools/*): keep the old
    // in-place behaviour rather than navigating to a route that does not exist.
    const newLang: Language = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    const url = new URL(window.location.href);
    if (newLang === 'es') url.searchParams.set('lang', 'es');
    else url.searchParams.delete('lang');
    window.history.replaceState({}, '', url.toString());
  };

  const t = getTranslation(language);

  return { language, toggleLanguage, t };
}
